import React, { Component, createRef } from "react";
import Gallery from "./Gallery";
import ExhibitGallery from "./ExhibitGallery";
import TeamMember from "./TeamMember";
import "./Home.scss";
import { CSSTransition } from "react-transition-group";
import { Helmet } from "react-helmet";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import ReactGA from "react-ga4";
import {
  disableScrollRestoration,
  resetPageScroll,
} from "./resetPageScroll";
import data from "./data/data.json";
import ashlyAboutMeImg from "./images/ashly-about-me-3.jpg";
import { ReactComponent as InstagramLogo } from "./images/instagram-glyph-black.svg";
import { ReactComponent as SubstackLogo } from "./images/substack-glyph.svg";

class Home extends Component {
  constructor(props) {
    super(props);

    const projectID = props.router.searchParams.get("p");

    this.state = {
      showProject: !!projectID,
      projectID: projectID || "",
      loaded: false,
    };

    this.gradientRef = createRef();
    this.introComplete = false;
  }

  componentDidMount() {
    this.setState({ loaded: true });
    window.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseleave", this.handleMouseLeave);

    if (!!this.state.projectID) {
      ReactGA.send({
        hitType: "pageview",
        page: "/projects/" + this.state.projectID,
        title: this.state.projectID,
      });
    } else {
      ReactGA.send({
        hitType: "pageview",
        page: "/",
        title: "Home",
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseleave", this.handleMouseLeave);
  }

  handleMouseLeave = () => {
    if (!this.introComplete || !this.gradientRef.current) return;
    this.gradientRef.current.style.transition =
      "background-position 0.6s ease-out";
    this.gradientRef.current.style.backgroundPosition = "80% center";
  };

  handleMouseMove = (e) => {
    if (!this.introComplete || !this.gradientRef.current) return;
    this.gradientRef.current.style.transition = "none";
    const x = 100 - (e.clientX / window.innerWidth) * 100;
    this.gradientRef.current.style.backgroundPosition = `${x}% center`;
  };

  handleGradientAnimationEnd = () => {
    this.introComplete = true;
    if (this.gradientRef.current) {
      this.gradientRef.current.style.backgroundPosition = "80% center";
      this.gradientRef.current.style.animation = "none";
    }
  };

  closeProject = () => {
    this.props.router.setSearchParams({});
    this.setState({ showProject: false });
  };

  openProject = (projectID) => {
    ReactGA.send({
      hitType: "pageview",
      page: "/projects/" + this.state.projectID,
      title: this.state.projectID,
    });
    this.props.router.setSearchParams({ p: projectID });
    this.setState({
      showProject: true,
      projectID: projectID,
    });
  };

  renderTeamMembers = () => {
    let teamMembers = data.team.map((t, idx) => (
      <TeamMember key={`team-${idx}`} teamMember={t} />
    ));
    return <div className="team-members">{teamMembers}</div>;
  };

  render() {
    return (
      <div>
        <Helmet bodyAttributes={{ style: "background-color: #1a1714" }} />
        {this.state.showProject && (
          <ExhibitGallery
            projectID={this.state.projectID}
            openProject={this.openProject}
            close={this.closeProject}
          />
        )}
        <div className="Home">
          <CSSTransition
            in={this.state.loaded}
            timeout={3000}
            classNames="header"
          >
            <header className="header">
              <div className="headings">
                <h1>
                  <span
                    className="bg-gradient"
                    ref={this.gradientRef}
                    onAnimationEnd={this.handleGradientAnimationEnd}
                  >
                    Ashly Edwards Huntington
                  </span>
                </h1>
                <div className="separator" />
                <h2>Writer, Storyteller, and Lifelong Learner.</h2>
              </div>
              <div className="hero-container">
                <div className="hero" />
              </div>
            </header>
          </CSSTransition>
          <main>
            <section className="purpose">
              <h1>Purpose.</h1>
              <p>
                Writers increase the focal length of the lens through which we
                view life. They provide us with new perspectives and experiences
                we might otherwise avoid and role models we've been unknowingly
                waiting for. Writers are the silent, behind-the-scenes teachers
                that softly prod us to think differently about the world, beyond
                what <em>is</em> and instead to what <em>could be</em>. It is
                there, outside the comforts of our own homes, that stories whet
                our curiosity, inspire empathy, and teach us. Whether a writer
                makes us laugh, cry, think, question, fear, or dream—they, above
                all else, have the responsibility to nurture our ability to
                connect.
              </p>
              <p>
                I believe that connection is the core of a better and brighter
                future for all.
              </p>
            </section>
            <section className="quote">
              <p>
                <span className="open-quote">"</span>
                <span className="quote-text">
                  Imagination will often carry us to worlds that never were. But
                  without it we go nowhere
                </span>
                <span className="author">Carl Sagan</span>
              </p>
            </section>
            <section>
              <h1>Select Projects.</h1>
              <Gallery openProject={this.openProject} />
            </section>
            <section className="purpose">
              <h1>About the Author.</h1>
              <a
                className="side-photo-link"
                href="https://umap.openstreetmap.fr/en/map/polar-2025_1388658#3/73.453473/-47.988281"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="side-photo"
                  src={ashlyAboutMeImg}
                  alt="Ashly Huntington"
                />
              </a>
              <p>
                Ashly Huntington is a storyteller driven by an insatiable
                curiosity about the unknown and a passion for bridging diverse
                fields such as microbiology, philosophy, neuroscience, frontier
                exploration, and biochemistry. Her work spans multiple
                genres—including comedy, drama, adventure, live-action, and
                animation—each infused with a commitment to authenticity and a
                deep exploration of the human condition. Whether through dark,
                introspective narratives or uplifting moments, her stories aim
                to probe the depths of the mind and soul, continuously pushing
                the boundaries of conventional storytelling.
              </p>
              <p>
                Ashly believes it is essential to expand narrative possibilities
                with new storytelling and production technologies without
                diminishing the intrinsic value of human experience. Her goal is
                to craft stories that not only entertain but also connect on a
                deeper level, fostering empathy and offering fresh perspectives
                on life's complexities.
              </p>
              <p>
                Beyond writing, Ashly is a perpetual student, deeply committed
                to academic rigor, especially in scientific fields. This
                dedication to learning fuels her creative and strategic
                pursuits, including playing classical piano, composing music,
                and engaging in strategy games like Civilization. Her
                exploration extends outdoors through hiking and archery, and is
                captured in{" "}
                <Link href="https://punchmagazine.com/perfect-shot-mist/">
                  her photography
                </Link>
                .
              </p>
            </section>
            <section className="purpose">
              <h1>Work.</h1>
              <p>
                Ashly writes about the construction of the self: the stories we
                inherit, the behaviors that earn approval, and the adaptations
                repeated so long they begin to feel like identity.
              </p>
              <p>
                She's interested in the machinery beneath conviction—how
                intuition, reason, desire, and experience conspire to produce
                what we call belief. The interplay between System 1's fast,
                intuitive judgments and System 2's deliberate reasoning runs
                throughout her work, as does reason's remarkable capacity to
                rationalize what we already want to believe.
                <sup>1</sup> Neither guarantees truth. Her characters are forced
                to ask not only what do I believe? but where did that belief
                come from, what is it protecting, and what does protecting it
                cost?
              </p>
              <p>
                Across her work, belonging is both a human need and a dangerous
                incentive. The desire to be seen can become the desire to be
                approved of, until performance and identity fuse. There is a
                paradox in that bargain: the more of yourself you forfeit to be
                recognized, the less of you remains to recognize. Her stories
                are interested in what happens when that bargain becomes
                visible—and whether seeing it is enough to change it.
              </p>
              <p>
                She's equally interested in capacity: what discipline,
                intelligence, and mastery make possible, and what they demand in
                return. Competence expands agency, but offers no immunity from
                self-deception. Without self-knowledge, greater intelligence can
                furnish our defenses with better arguments.
              </p>
              <p>
                Her work resists the idea that rationality and emotionality are
                opposing forces. Reason can interrogate what emotion perceives;
                empathy can reveal what analysis alone cannot. Each can correct
                the other's distortions. What matters is not which faculty wins,
                but whether a person can use both without mistaking either for
                the whole truth.
              </p>
              <p>
                These questions aren't reserved for adult audiences. Ashly
                doesn't believe writing for children requires making ideas
                smaller. Complexity is a problem of translation, not capacity.
                The task is not to diminish the idea, but to find the form.
              </p>
              <p>
                Her stories make room for humor alongside grief, fear, and
                tragedy—not as an escape, but as one of the ways we remain human
                within it.
              </p>
              <p>
                Ultimately, her work is interested in the boundaries of the
                self: what we inherit, what we learn to perform, what we defend,
                and what we mistake for choice. Once we gain the vantage point
                to see those forces, story can do more than describe the
                boundaries they have made. It can search for a way beyond
                them—toward worlds we could not previously imagine, forms of
                understanding we did not yet possess, and possibilities for
                being that were invisible from where we began.
              </p>
              <p className="work-note">
                <sup>1</sup> Daniel Kahneman,{" "}
                <em>Thinking, Fast and Slow</em> (2011).
              </p>
            </section>
            <section className="shelf-promo">
              <RouterLink
                to="/the-shelf"
                className="shelf-link"
                onClick={() => {
                  disableScrollRestoration();
                  resetPageScroll();
                }}
              >
                The Book Nook
              </RouterLink>
              <RouterLink
                to="/screening-room"
                className="shelf-link"
                onClick={() => {
                  disableScrollRestoration();
                  resetPageScroll();
                }}
              >
                Favorite Cinema
              </RouterLink>
            </section>
            <section className="contact">
              <h1>Team.</h1>
              {this.renderTeamMembers()}
            </section>
          </main>
          <footer>
            <p className="social">
              <a
                href="https://www.instagram.com/aehgallery/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginRight: "20px" }}
              >
                <InstagramLogo className="icon" />
              </a>
              <a
                href="https://substack.com/@edwardshuntington?r=26z4v7&utm_campaign=profile&utm_medium=profile-page"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SubstackLogo className="icon" />
              </a>
            </p>
            <p>
              <span>&mdash;&mdash;</span>
              <span>
                Website &amp; Graphics by
                <br />
                Ashly Huntington &amp;{" "}
                <a
                  href="http://ryanatallah.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ryan Atallah
                </a>
              </span>{" "}
              <span>&mdash;&mdash;</span>
            </p>
          </footer>
        </div>
      </div>
    );
  }
}

function Link({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export const withSearchParams = (Component) => {
  function ComponentWithRouterProp(props) {
    let [searchParams, setSearchParams] = useSearchParams();
    return <Component {...props} router={{ searchParams, setSearchParams }} />;
  }
  return ComponentWithRouterProp;
};

export default withSearchParams(Home);
